<template>
  <div class="dashboard">
    <!-- Welcome banner -->
    <div class="welcome-banner mb-4">
      <div class="welcome-text">
        <div class="caption text-uppercase grey--text">{{ today }}</div>
        <h2 class="mb-1">
          Welcome back 👋
          <v-chip class="ml-2" :color="roleColor" small dark>
            <v-icon left small>{{ roleIcon }}</v-icon>
            {{ roleLabel }}
          </v-chip>
        </h2>
        <div class="grey--text">{{ scopeBlurb }}</div>
      </div>
      <div class="welcome-actions" v-if="canIssue || canClaim">
        <v-btn v-if="canIssue" color="primary" depressed to="/travel">
          <v-icon left>mdi-plus</v-icon> New Policy
        </v-btn>
        <v-btn v-if="canClaim" color="success" depressed to="/claim/new" class="ml-2">
          <v-icon left>mdi-file-document-plus</v-icon> New Claim
        </v-btn>
      </div>
    </div>

    <!-- KPI cards -->
    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-blue" elevation="2">
          <div class="kpi-icon"><v-icon dark large>mdi-airplane-clock</v-icon></div>
          <div class="kpi-body">
            <div class="kpi-label">Total Policies</div>
            <div class="kpi-value">{{ totalPolicy | numeral('0,0') }}</div>
            <div class="kpi-foot">all time</div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-green" elevation="2">
          <div class="kpi-icon"><v-icon dark large>mdi-cash-multiple</v-icon></div>
          <div class="kpi-body">
            <div class="kpi-label">Total GWP</div>
            <div class="kpi-value">{{ totalPremium | numeral('0,0') }}</div>
            <div class="kpi-foot">LAK</div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-orange" elevation="2">
          <div class="kpi-icon"><v-icon dark large>mdi-calendar-month</v-icon></div>
          <div class="kpi-body">
            <div class="kpi-label">This Month</div>
            <div class="kpi-value">{{ thisMonth | numeral('0,0') }}</div>
            <div class="kpi-foot">
              <span :class="trendClass">
                <v-icon x-small :color="trendColor">{{ trendIcon }}</v-icon>
                {{ trendPct }}% vs last month
              </span>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- 4th KPI is a stacked Claims + Refunds card -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card kpi-red" elevation="2">
          <div class="kpi-icon"><v-icon dark large>mdi-clipboard-alert</v-icon></div>
          <div class="kpi-body" style="width:100%">
            <div class="kpi-label">Pending Workload</div>
            <div class="d-flex justify-space-between align-baseline">
              <router-link :to="'/claim'" class="white--text" style="text-decoration:none">
                <div class="kpi-value-sm">{{ claimStats.pending | numeral('0,0') }}</div>
                <div class="kpi-foot">claims</div>
              </router-link>
              <div class="kpi-divider"></div>
              <router-link :to="'/refund'" class="white--text" style="text-decoration:none">
                <div class="kpi-value-sm">{{ refundStats.pending | numeral('0,0') }}</div>
                <div class="kpi-foot">refunds</div>
              </router-link>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Chart + Quick Actions -->
    <v-row dense class="mt-2">
      <v-col cols="12" md="8">
        <v-card elevation="2">
          <v-card-title class="py-3">
            <v-icon left color="primary">mdi-chart-bell-curve-cumulative</v-icon>
            Monthly Trend
            <v-spacer></v-spacer>
            <span class="caption grey--text">Premium written per month (LAK) — {{ currentYear }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div v-if="chart_data_loaded" style="height:320px">
              <line-chart :chart-data="line_chart"></line-chart>
            </div>
            <div v-else class="text-center pa-8 grey--text">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-2">Loading chart…</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card elevation="2">
          <v-card-title class="py-3">
            <v-icon left color="primary">mdi-lightning-bolt</v-icon>
            Quick Actions
          </v-card-title>
          <v-divider></v-divider>
          <v-list dense>
            <v-list-item v-for="action in quickActions" :key="action.to" :to="action.to" link>
              <v-list-item-avatar :color="action.color" size="36">
                <v-icon dark small>{{ action.icon }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ action.title }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ action.desc }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon><v-icon small>mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Average premium card -->
        <v-card elevation="2" class="mt-3">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <v-avatar color="indigo lighten-5" size="48" class="mr-3">
                <v-icon color="indigo">mdi-calculator-variant</v-icon>
              </v-avatar>
              <div>
                <div class="caption grey--text">Average Premium / Policy</div>
                <div class="text-h6 font-weight-bold indigo--text">
                  {{ avgPremium | numeral('0,0') }} <span class="caption grey--text">LAK</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Policy by Province (full width — 100% policy) -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="py-3">
            <v-icon left color="primary">mdi-map-marker-multiple</v-icon>
            Policy by Province
            <v-spacer></v-spacer>
            <span class="caption grey--text">{{ byProvince.length }} province{{ byProvince.length === 1 ? '' : 's' }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="byProvince.length === 0" class="text-center grey--text py-6">
            No policy data yet.
          </v-card-text>
          <v-row v-else class="ma-0 pa-2" dense>
            <v-col v-for="p in byProvince" :key="p.province_id" cols="12" sm="6" md="4" lg="3">
              <v-card outlined class="province-card">
                <v-card-text class="pa-3">
                  <div class="d-flex align-center mb-2">
                    <v-avatar color="indigo lighten-5" size="36" class="mr-2">
                      <v-icon color="indigo" small>mdi-map-marker-radius</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-bold">{{ p.province_name || 'Unknown' }}</div>
                      <div class="caption grey--text">{{ p.border_count }} border{{ p.border_count === 1 ? '' : 's' }}</div>
                    </div>
                  </div>
                  <div class="d-flex justify-space-between align-baseline">
                    <span class="caption grey--text">Policies</span>
                    <strong class="primary--text">{{ p.policy_count | numeral('0,0') }}</strong>
                  </div>
                  <div class="d-flex justify-space-between align-baseline">
                    <span class="caption grey--text">Premium</span>
                    <strong class="green--text">{{ p.total_premium | numeral('0,0') }} <span class="caption">LAK</span></strong>
                  </div>
                  <v-progress-linear
                    :value="provincePct(p.policy_count)"
                    color="indigo" height="6" rounded class="mt-2"
                  ></v-progress-linear>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Claims + Refunds side-by-side -->
    <v-row dense class="mt-2">
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100">
          <v-card-title class="py-3">
            <v-icon left color="red darken-1">mdi-file-document-multiple-outline</v-icon>
            Claims Status
            <v-spacer></v-spacer>
            <v-btn x-small text :to="'/claim'" color="primary">
              View all <v-icon right x-small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-2">
            <div class="claim-totals mb-2 pa-3">
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Total Claimed</span>
                <strong class="primary--text">{{ claimStats.claimed_total | numeral('0,0') }} LAK</strong>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Approved</span>
                <strong class="green--text">{{ claimStats.approved_total | numeral('0,0') }} LAK</strong>
              </div>
            </div>
            <v-list dense class="status-list">
              <v-list-item v-for="s in claimStatusRows" :key="s.key" class="px-2">
                <v-list-item-avatar size="28" :color="s.color" class="mr-2">
                  <v-icon dark x-small>{{ s.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="caption">{{ s.label }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <strong>{{ s.count | numeral('0,0') }}</strong>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100">
          <v-card-title class="py-3">
            <v-icon left color="pink darken-1">mdi-cash-refund</v-icon>
            Refunds Status
            <v-spacer></v-spacer>
            <v-btn x-small text :to="'/refund'" color="primary">
              View all <v-icon right x-small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-2">
            <div class="refund-totals mb-2 pa-3">
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Awaiting Review</span>
                <strong class="orange--text">{{ refundStats.requested_total | numeral('0,0') }} LAK</strong>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Approved (to pay)</span>
                <strong class="blue--text">{{ refundStats.approved_pending_total | numeral('0,0') }} LAK</strong>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Refunded</span>
                <strong class="green--text">{{ refundStats.refunded_total | numeral('0,0') }} LAK</strong>
              </div>
            </div>
            <v-list dense class="status-list">
              <v-list-item v-for="s in refundStatusRows" :key="s.key" class="px-2">
                <v-list-item-avatar size="28" :color="s.color" class="mr-2">
                  <v-icon dark x-small>{{ s.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="caption">{{ s.label }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <strong>{{ s.count | numeral('0,0') }}</strong>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Policy by Border (full-width table — 100% policy) -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="py-3">
            <v-icon left color="primary">mdi-bridge</v-icon>
            Top Borders by Policy Volume
            <v-spacer></v-spacer>
            <span class="caption grey--text">Top {{ byBorder.length }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-simple-table dense>
            <thead class="grey lighten-4">
              <tr>
                <th>#</th>
                <th>Border</th>
                <th>Province</th>
                <th class="text-right">Policies</th>
                <th class="text-right">Premium (LAK)</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="byBorder.length === 0">
                <td colspan="6" class="text-center grey--text py-4">No border data yet.</td>
              </tr>
              <tr v-for="(b, i) in byBorder" :key="b.border_id">
                <td>{{ i + 1 }}</td>
                <td><strong>{{ b.border_name || 'Unknown' }}</strong></td>
                <td class="grey--text">{{ b.province_name || '—' }}</td>
                <td class="text-right">{{ b.policy_count | numeral('0,0') }}</td>
                <td class="text-right green--text">{{ b.total_premium | numeral('0,0') }}</td>
                <td style="width:25%">
                  <v-progress-linear
                    :value="(b.policy_count / (maxBorderCount || 1)) * 100"
                    color="primary" height="8" rounded
                  ></v-progress-linear>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Monthly breakdown table -->
    <v-row dense class="mt-2">
      <v-col cols="12">
        <v-card elevation="2">
          <v-card-title class="py-3">
            <v-icon left color="primary">mdi-table</v-icon>
            Monthly Breakdown
            <v-spacer></v-spacer>
            <span class="caption grey--text">{{ currentYear }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-simple-table dense>
            <thead class="grey lighten-4">
              <tr>
                <th>Month</th>
                <th class="text-right">Premium (LAK)</th>
                <th class="text-right">% of Year</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(label, i) in monthLabels" :key="label">
                <td>{{ label }}</td>
                <td class="text-right">{{ monthData[i] | numeral('0,0') }}</td>
                <td class="text-right">{{ pct(monthData[i]) }}%</td>
                <td>
                  <v-progress-linear
                    :value="(monthData[i] / (maxMonth || 1)) * 100"
                    :color="i === currentMonthIdx ? 'orange' : 'primary'"
                    height="8" rounded
                  ></v-progress-linear>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import LineChart from "@/components/LineChart";

export default {
  name: "DashBoard",
  components: { LineChart },
  data: () => ({
    chart_data_loaded: false,
    travel_count: {},
    monthLabels: [],
    monthData: [],
    apiRole: null,
    apiProvinceName: null,
    byBorder: [],
    byProvince: [],
    claimStats: {
      total: 0,
      pending: 0,
      claimed_total: 0,
      approved_total: 0,
      by_status: {
        submitted: 0, under_review: 0, info_required: 0,
        approved: 0, partial_approved: 0, rejected: 0, paid: 0
      }
    },
    refundStats: {
      total: 0,
      pending: 0,
      requested_total: 0,
      approved_pending_total: 0,
      refunded_total: 0,
      rejected_total: 0,
      by_status: { requested: 0, approved: 0, rejected: 0, refunded: 0 }
    },
    line_chart: {
      labels: [],
      datasets: [{
        label: 'Monthly Premium (LAK)',
        backgroundColor: 'rgba(255, 152, 0, 0.15)',
        borderColor: '#FF9800',
        borderWidth: 2,
        pointBackgroundColor: '#FF9800',
        pointRadius: 4,
        lineTension: 0.35,
        fill: true,
        data: []
      }]
    }
  }),
  computed: {
    today() {
      return this.$moment().format('dddd, D MMMM YYYY');
    },
    role() {
      return this.apiRole || this.$store.getters.userRole || 'user';
    },
    roleLabel() {
      return ({
        admin: 'Administrator',
        government: 'Government',
        local_government: 'Local Government',
        user: 'Agent'
      })[this.role] || this.role;
    },
    roleIcon() {
      return ({
        admin: 'mdi-shield-crown',
        government: 'mdi-bank',
        local_government: 'mdi-map-marker-radius',
        user: 'mdi-account'
      })[this.role] || 'mdi-account';
    },
    roleColor() {
      return ({
        admin: 'red darken-2',
        government: 'indigo',
        local_government: 'teal',
        user: 'amber darken-2'
      })[this.role] || 'grey';
    },
    scopeBlurb() {
      if (this.role === 'admin')            return 'Full system access — viewing all data across all provinces.';
      if (this.role === 'government')       return 'Nationwide oversight — viewing aggregated data for all provinces.';
      if (this.role === 'local_government') return `Provincial oversight${this.apiProvinceName ? ' — ' + this.apiProvinceName : ''}.`;
      return "Here's an overview of your travel insurance activity.";
    },
    canIssue() { return this.role === 'user' || this.role === 'admin'; },
    canClaim() { return this.role === 'user' || this.role === 'admin'; },
    currentYear() {
      return new Date().getFullYear();
    },
    currentMonthIdx() {
      return new Date().getMonth();
    },
    totalPolicy() {
      return parseInt(this.travel_count.total_policy) || 0;
    },
    totalPremium() {
      return parseInt(this.travel_count.total_premium) || 0;
    },
    avgPremium() {
      return this.totalPolicy ? Math.round(this.totalPremium / this.totalPolicy) : 0;
    },
    thisMonth() {
      return this.monthData[this.currentMonthIdx] || 0;
    },
    lastMonth() {
      const idx = this.currentMonthIdx - 1;
      return idx >= 0 ? (this.monthData[idx] || 0) : 0;
    },
    ytdTotal() {
      return this.monthData.slice(0, this.currentMonthIdx + 1).reduce((s, n) => s + (n || 0), 0);
    },
    ytdAvg() {
      const months = this.currentMonthIdx + 1;
      return months ? Math.round(this.ytdTotal / months) : 0;
    },
    maxMonth() {
      return Math.max(...this.monthData, 1);
    },
    trendPct() {
      if (!this.lastMonth) return this.thisMonth > 0 ? 100 : 0;
      return Math.round(((this.thisMonth - this.lastMonth) / this.lastMonth) * 100);
    },
    trendIcon() {
      return this.trendPct >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },
    trendColor() {
      return this.trendPct >= 0 ? 'green' : 'red';
    },
    trendClass() {
      return this.trendPct >= 0 ? 'green--text' : 'red--text';
    },
    maxBorderCount() {
      return this.byBorder.reduce((m, b) => Math.max(m, Number(b.policy_count) || 0), 0);
    },
    maxProvinceCount() {
      return this.byProvince.reduce((m, p) => Math.max(m, Number(p.policy_count) || 0), 0);
    },
    claimStatusRows() {
      const s = this.claimStats.by_status || {};
      return [
        { key: 'submitted',        label: 'Submitted',        count: s.submitted        || 0, color: 'blue',           icon: 'mdi-file-send' },
        { key: 'under_review',     label: 'Under Review',     count: s.under_review     || 0, color: 'orange',         icon: 'mdi-magnify' },
        { key: 'info_required',    label: 'Info Required',    count: s.info_required    || 0, color: 'amber darken-2', icon: 'mdi-alert-circle' },
        { key: 'approved',         label: 'Approved',         count: s.approved         || 0, color: 'green',          icon: 'mdi-check-circle' },
        { key: 'partial_approved', label: 'Partial',          count: s.partial_approved || 0, color: 'teal',           icon: 'mdi-circle-half-full' },
        { key: 'rejected',         label: 'Rejected',         count: s.rejected         || 0, color: 'red',            icon: 'mdi-close-circle' },
        { key: 'paid',             label: 'Paid',             count: s.paid             || 0, color: 'green darken-2', icon: 'mdi-cash-check' }
      ];
    },
    refundStatusRows() {
      const s = this.refundStats.by_status || {};
      return [
        { key: 'requested', label: 'Requested', count: s.requested || 0, color: 'orange',         icon: 'mdi-file-send' },
        { key: 'approved',  label: 'Approved',  count: s.approved  || 0, color: 'blue',           icon: 'mdi-check-circle' },
        { key: 'rejected',  label: 'Rejected',  count: s.rejected  || 0, color: 'red',            icon: 'mdi-close-circle' },
        { key: 'refunded',  label: 'Refunded',  count: s.refunded  || 0, color: 'green darken-2', icon: 'mdi-cash-check' }
      ];
    },
    quickActions() {
      const all = [
        { title: 'New Travel Policy', desc: 'Issue a new travel policy',  icon: 'mdi-airplane-plus',        color: 'primary', to: '/travel',        show: this.canIssue },
        { title: 'Submit a Claim',    desc: 'File a new claim',           icon: 'mdi-file-document-plus',   color: 'success', to: '/claim/new',     show: this.canClaim },
        { title: 'View Claims',       desc: this.role === 'user' ? 'Review claims you submitted' : 'Review claims in scope', icon: 'mdi-file-document-multiple', color: 'teal', to: '/claim', show: true },
        { title: 'Request Refund',    desc: 'Refund a policy before the effective date', icon: 'mdi-cash-refund', color: 'red lighten-1', to: '/refund/new', show: this.canIssue },
        { title: 'View Refunds',      desc: 'Browse refund requests in scope', icon: 'mdi-cash-multiple', color: 'pink', to: '/refund', show: true },
        { title: 'Search Policies',   desc: 'Find a policy by number or name', icon: 'mdi-magnify',         color: 'indigo',  to: '/travel-search', show: true },
        { title: 'Reports',           desc: 'View travel sales report',    icon: 'mdi-chart-bar',           color: 'orange',  to: '/travel-report', show: true }
      ];
      return all.filter(a => a.show);
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    pct(n) {
      const total = this.monthData.reduce((s, x) => s + (x || 0), 0);
      if (!total) return 0;
      return ((n / total) * 100).toFixed(1);
    },
    provincePct(count) {
      const max = this.maxProvinceCount || 1;
      return Math.min(100, (count / max) * 100);
    },
    async getData() {
      try {
        this.chart_data_loaded = false;
        const r = await window.axios.get('travel/board_scope');
        this.monthLabels = r.data.travel.title || [];
        this.monthData = r.data.travel.amount || [];
        this.line_chart.labels = this.monthLabels;
        this.line_chart.datasets[0].data = this.monthData;
        this.travel_count = (r.data.count_data && r.data.count_data[0]) || {};
        this.apiRole = r.data.role || null;
        this.apiProvinceName = r.data.province_name || null;
        this.byBorder = r.data.by_border || [];
        this.byProvince = r.data.by_province || [];
        if (r.data.claim_stats) {
          this.claimStats = { ...this.claimStats, ...r.data.claim_stats };
        }
        if (r.data.refund_stats) {
          this.refundStats = { ...this.refundStats, ...r.data.refund_stats };
        }
        this.chart_data_loaded = true;
      } catch (e) {
        console.error(e);
        this.chart_data_loaded = true;
      }
    }
  }
};
</script>

<style scoped>
.welcome-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 22px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4ebf3 100%);
  border-radius: 10px;
  border: 1px solid #e0e6ed;
}
.welcome-text h2 { font-weight: 600; }

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  padding: 18px;
  min-height: 110px;
  border-radius: 10px;
  color: white;
  overflow: hidden;
}
.kpi-card::after {
  content: "";
  position: absolute;
  right: -30px;
  bottom: -30px;
  width: 120px;
  height: 120px;
  background: rgba(255,255,255,0.12);
  border-radius: 50%;
}
.kpi-icon {
  width: 56px;
  height: 56px;
  background: rgba(255,255,255,0.22);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  z-index: 1;
}
.kpi-body { z-index: 1; }
.kpi-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.85;
}
.kpi-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 2px;
}
.kpi-foot {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 4px;
}

.kpi-blue   { background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%); }
.kpi-green  { background: linear-gradient(135deg, #43a047 0%, #1b5e20 100%); }
.kpi-orange { background: linear-gradient(135deg, #ff9800 0%, #e65100 100%); }
.kpi-purple { background: linear-gradient(135deg, #9c27b0 0%, #6a1b9a 100%); }
.kpi-red    { background: linear-gradient(135deg, #ef5350 0%, #b71c1c 100%); }
.kpi-value-sm { font-size: 22px; font-weight: 700; line-height: 1.1; }
.kpi-divider {
  width: 1px; background: rgba(255,255,255,0.4); margin: 0 12px;
  align-self: stretch;
}

.province-card { transition: box-shadow 0.2s ease; }
.province-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.claim-totals {
  background: #fce4ec;
  border-radius: 6px;
}
.refund-totals {
  background: #fff3e0;
  border-radius: 6px;
}
.h-100 { height: 100%; }
.status-list .v-list-item { min-height: 32px; }
</style>
